import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { tokenStore } from "../../lib/tokenStore.js";
import { ottoBotsApi } from "./api.js";
import { BotCard } from "./bots-panel/BotCard.js";
import { CreateBotDialog } from "./bots-panel/CreateBotDialog.js";
import { SecretRevealDialog } from "./bots-panel/SecretRevealDialog.js";
import type { OttoBot, OttoBotWithSecret } from "./types.js";

/**
 * System Admin ▸ Otto Bots (`/admin/otto`).
 *
 * Lists every OttoBot, surfaces its embed snippet, allows create /
 * soft-disable. This panel renders WITHOUT its own `<PageHeader>` — the
 * shared "System Admin" header + tab strip live in `AdminLayout.tsx`.
 *
 * The signing_secret is shown EXACTLY ONCE after a successful create
 * or rotate-secret call (the `SecretRevealDialog`); closing it loses the
 * secret forever.
 *
 * This file is the data orchestrator: it loads the bot list + owns the
 * create / disable flow. The bot card lives in `bots-panel/BotCard`, the
 * create form in `CreateBotDialog`, the one-time secret reveal in
 * `SecretRevealDialog`, and the embed-origin helper in `otSrcOrigin`.
 */
export function BotsPanel(): JSX.Element {
  const accessToken = tokenStore.getAccess();
  const authHeader = accessToken ? `Bearer ${accessToken}` : undefined;

  const [bots, setBots] = useState<OttoBot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [mintedSecret, setMintedSecret] = useState<OttoBotWithSecret | null>(null);
  const [pendingDisable, setPendingDisable] = useState<OttoBot | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    setBots(null);
    try {
      const list = await ottoBotsApi.list(authHeader);
      setBots(list);
    } catch (err) {
      // The proxy returns a typed, human-readable `error` string
      // (gateway-unreachable, not-configured, upstream HTTP). Surface
      // that — never a raw `[object Object]` / "Error: …" dump.
      setBots([]);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [authHeader]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            id="otto-bots-heading"
            sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
          >
            Otto Bots
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
            Multi-bot Otto deployments. Each bot has its own embed key,
            knowledge scope, and host-origin allowlist. The
            customer-support bot is locked to customer-only knowledge;
            the staff-internal bot also reads internal docs.
          </Typography>
        </Box>
        <Button
          type="button"
          variant="contained"
          onClick={() => setShowCreate(true)}
          sx={{ flexShrink: 0 }}
        >
          + New bot
        </Button>
      </Box>

      {error && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            bgcolor: (t) => alpha(t.palette.error.main, 0.1),
            px: 2,
            py: 1.5,
            fontSize: 14,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>
            Couldn’t load Otto bots
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>{error}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
            If this says the gateway is unreachable or the bots aren’t
            configured, run <Box component="code">python scripts/seed_bots.py</Box> on
            the AI gateway and confirm <Box component="code">AI_GATEWAY_URL</Box> is
            set for this dashboard.
          </Typography>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => void refresh()}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Box>
      )}

      {bots === null && !error && (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>Loading…</Typography>
      )}

      {bots && bots.length === 0 && !error && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            px: 2,
            py: 3,
            textAlign: "center",
            fontSize: 14,
            color: "text.disabled",
          }}
        >
          No bots yet. Run the gateway’s <Box component="code">scripts/seed_bots.py</Box> or click “+ New bot”.
        </Box>
      )}

      {bots && bots.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" },
          }}
        >
          {bots.map((bot) => (
            <BotCard
              key={bot.id}
              bot={bot}
              onSoftDelete={() => setPendingDisable(bot)}
            />
          ))}
        </Box>
      )}

      {showCreate && (
        <CreateBotDialog
          onClose={() => setShowCreate(false)}
          onCreated={(bot) => {
            setMintedSecret(bot);
            setShowCreate(false);
            void refresh();
          }}
          {...(authHeader ? { authHeader } : {})}
        />
      )}

      {mintedSecret && (
        <SecretRevealDialog
          bot={mintedSecret}
          onClose={() => setMintedSecret(null)}
        />
      )}

      <ConfirmDialog
        isOpen={pendingDisable !== null}
        onClose={() => setPendingDisable(null)}
        title={`Disable bot ${pendingDisable?.slug ?? ""}?`}
        description="The bot stops serving immediately. It can be re-enabled from the detail view; its audit history is kept."
        confirmLabel="Disable bot"
        onConfirm={async () => {
          if (!pendingDisable) return;
          try {
            await ottoBotsApi.remove(pendingDisable.id, authHeader);
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            throw err; // keep the dialog open so the user can retry
          }
          setPendingDisable(null);
          await refresh();
        }}
      />
    </Box>
  );
}
